import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient, { getTrialStatus } from '../api/client';

function TopicTests() {
  const [selectedSubject, setSelectedSubject] = useState('Reading');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [userPremiumStatus, setUserPremiumStatus] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [userExamType, setUserExamType] = useState('selective');
  const [trialStatus, setTrialStatus] = useState({
    trialTopicsRemaining: 0,
    hasTrialAccess: false
  });
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [allExams, setAllExams] = useState([]);
  const [groupedTests, setGroupedTests] = useState({});

  // Subject configuration with colors matching ExamList
  const subjectConfig = {
    'Reading': { color: '#10b981', icon: '📖' },
    'Mathematical Reasoning': { color: '#4f46e5', icon: '🔢' },
    'Thinking Skills': { color: '#f59e0b', icon: '🧠' },
    'Writing': { color: '#ef4444', icon: '✍️' }
  };

  useEffect(() => {
    checkPremiumStatus();
    fetchTrialStatus();
    fetchExams();
    
    // Check for subject query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const subjectParam = urlParams.get('subject');
    if (subjectParam && subjects.includes(subjectParam)) {
      setSelectedSubject(subjectParam);
    }
  }, []);

  const fetchExams = async () => {
    try {
      const response = await apiClient.get('/exams');
      const topicBasedExams = response.data.filter(exam => exam.category === 'TopicBased');
      setAllExams(topicBasedExams);
      groupTestsByTopic(topicBasedExams);
    } catch (err) {
      console.error('Error fetching exams:', err);
    }
  };

  const groupTestsByTopic = (exams) => {
    const grouped = {};
    
    exams.forEach(exam => {
      // Normalize topic name: remove OC_ prefix for grouping
      // OC_Reading -> Reading, OC_MathematicalReasoning -> Mathematical Reasoning
      let subject = exam.topic;
      if (subject.startsWith('OC_')) {
        subject = subject.replace('OC_', '');
        // Convert MathematicalReasoning to Mathematical Reasoning
        if (subject === 'MathematicalReasoning') {
          subject = 'Mathematical Reasoning';
        } else if (subject === 'ThinkingSkills') {
          subject = 'Thinking Skills';
        }
      } else {
        // For Selective exams, also normalize
        if (subject === 'MathematicalReasoning') {
          subject = 'Mathematical Reasoning';
        } else if (subject === 'ThinkingSkills') {
          subject = 'Thinking Skills';
        }
      }
      
      // Extract base topic name by removing " (Test 2)" or " (Test 3)" from title if topicName is missing
      let topicName = exam.topicName;
      if (!topicName) {
        // Fallback: extract from title by removing " (Test X)" suffix
        topicName = exam.title.replace(/ \(Test \d+\)$/, '');
      }
      
      if (!grouped[subject]) {
        grouped[subject] = {};
      }
      
      if (!grouped[subject][topicName]) {
        grouped[subject][topicName] = [];
      }
      
      grouped[subject][topicName].push(exam);
    });
    
    setGroupedTests(grouped);
  };

  const checkPremiumStatus = async () => {
    try {
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      
      const premiumVal = attributes['custom:premium_status'] || 'false';
      const isPremium = premiumVal === 'true' || premiumVal === 'essential';
      setUserPremiumStatus(isPremium);
      if (premiumVal === 'essential') setUserTier('essential');
      else if (premiumVal === 'true' || premiumVal === 'premium') setUserTier('premium');
      else setUserTier('free');
      
      setUserExamType(attributes['custom:exam_type'] || 'selective');
    } catch (err) {
      console.error('Error checking premium status:', err);
      setUserPremiumStatus(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const status = await getTrialStatus();
      setTrialStatus(status);
    } catch (err) {
      console.error('Error fetching trial status:', err);
    } finally {
      setCheckingAccess(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const subjects = userExamType === 'oc' 
    ? ['Reading', 'Mathematical Reasoning', 'Thinking Skills']
    : ['Reading', 'Mathematical Reasoning', 'Thinking Skills', 'Writing'];

  // Get current subject config
  const getCurrentSubjectConfig = () => {
    return subjectConfig[selectedSubject] || { color: '#4f46e5', icon: '📚' };
  };

  // Get topics for selected subject
  const getTopicsForSubject = () => {
    const subjectTests = groupedTests[selectedSubject] || {};
    return Object.entries(subjectTests)
      .map(([topicName, tests]) => ({
        topicName,
        tests: tests.sort((a, b) => {
          // Sort tests by examId to get Test 1, Test 2, Test 3 in order
          const getTestNumber = (examId) => {
            // Check for -3 or -2 suffix, otherwise it's test 1
            const match = examId.match(/-(\d+)$/);
            return match ? parseInt(match[1]) : 1;
          };
          return getTestNumber(a.examId) - getTestNumber(b.examId);
        }),
        testCount: tests.length
      }))
      .sort((a, b) => a.topicName.localeCompare(b.topicName)); // Sort topics alphabetically
  };

  if (loading || checkingAccess) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading tests...</p>
      </div>
    );
  }

  // Block access if not premium AND no trial topics remaining
  if (!userPremiumStatus && trialStatus.trialTopicsRemaining === 0) {
    return (
      <div style={{ maxWidth: '800px', margin: '60px auto', padding: '20px' }}>
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '60px 40px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: 'white'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🔒</div>
          <h1 style={{ fontSize: '36px', marginBottom: '20px' }}>Premium Access Required</h1>
          <p style={{ fontSize: '18px', marginBottom: '30px', opacity: '0.95', lineHeight: '1.7' }}>
            Topic-based tests are available exclusively to premium members. 
            Upgrade your account to access targeted practice for specific skills and topics.
          </p>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            padding: '25px', 
            borderRadius: '12px',
            marginBottom: '30px'
          }}>
            <h3 style={{ fontSize: '20px', marginBottom: '15px' }}>Premium Benefits:</h3>
            <ul style={{ 
              textAlign: 'left', 
              fontSize: '16px', 
              lineHeight: '2',
              listStyle: 'none',
              padding: 0
            }}>
              <li>✅ Access to all topic-based practice tests</li>
              <li>✅ Targeted skill development</li>
              <li>✅ Detailed performance analytics</li>
              <li>✅ Priority support</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/pricing" style={{ textDecoration: 'none' }}>
              <button 
                className="btn-primary"
                style={{ 
                  background: 'white',
                  color: '#ef4444',
                  padding: '15px 40px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                View Pricing
              </button>
            </Link>
            <Link to="/exams" style={{ textDecoration: 'none' }}>
              <button 
                className="btn-secondary"
                style={{ 
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '2px solid white',
                  padding: '15px 40px',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                Try Free Tests
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '40px auto', padding: '20px' }}>
      {/* Header */}
      <div className="card" style={{ 
        textAlign: 'center', 
        padding: '50px 24px', 
        marginBottom: '30px',
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        color: 'white'
      }}>
        <div style={{ fontSize: '56px', marginBottom: '15px' }}>📚</div>
        <h1 style={{ fontSize: '42px', marginBottom: '15px' }}>Practice by Topic</h1>
        <p style={{ fontSize: '20px', opacity: '0.95', maxWidth: '700px', margin: '0 auto' }}>
          Focus on specific skills with targeted practice tests. Master one topic at a time!
        </p>
      </div>

      {/* Subject Selector */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '15px', color: getCurrentSubjectConfig().color, textAlign: 'center' }}>
          Choose a Subject
        </h2>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {subjects.map(subject => {
            const config = subjectConfig[subject];
            return (
              <button
                key={subject}
                onClick={() => setSelectedSubject(subject)}
                style={{
                  padding: '15px 30px',
                  borderRadius: '12px',
                  border: selectedSubject === subject ? `3px solid ${config.color}` : '3px solid #e5e7eb',
                  background: selectedSubject === subject ? config.color : 'white',
                  color: selectedSubject === subject ? 'white' : config.color,
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: selectedSubject === subject ? `0 6px 12px ${config.color}40` : '0 2px 4px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedSubject !== subject) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }
                }}
              >
                {config.icon} {subject}
              </button>
            );
          })}
        </div>
      </div>

      {/* Topics List - Dynamic from Database */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {getTopicsForSubject().map((topicGroup, index) => {
          const categoryId = `${selectedSubject}-${index}`;
          const isExpanded = expandedCategories[categoryId];
          const currentConfig = getCurrentSubjectConfig();
          
          return (
            <div key={categoryId} className="card" style={{ padding: '0', overflow: 'hidden', border: `2px solid ${currentConfig.color}40` }}>
              {/* Topic Header - Clickable */}
              <div 
                onClick={() => toggleCategory(categoryId)}
                style={{
                  padding: '25px 30px',
                  background: isExpanded ? currentConfig.color : `${currentConfig.color}10`,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = `${currentConfig.color}20`;
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = `${currentConfig.color}10`;
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                  <div style={{ fontSize: '48px' }}>{currentConfig.icon}</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      fontSize: '24px', 
                      marginBottom: '8px', 
                      color: isExpanded ? 'white' : currentConfig.color,
                      fontWeight: 'bold'
                    }}>
                      {topicGroup.topicName}
                    </h3>
                    <p style={{ 
                      fontSize: '16px', 
                      color: isExpanded ? 'rgba(255,255,255,0.9)' : '#666',
                      margin: 0
                    }}>
                      {topicGroup.testCount} {topicGroup.testCount === 1 ? 'test' : 'tests'} available
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '10px 20px',
                    background: isExpanded ? 'rgba(255,255,255,0.2)' : 'white',
                    borderRadius: '8px',
                    border: isExpanded ? '2px solid rgba(255,255,255,0.3)' : `2px solid ${currentConfig.color}40`
                  }}>
                    <div style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold', 
                      color: isExpanded ? 'white' : currentConfig.color
                    }}>
                      {topicGroup.testCount}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: isExpanded ? 'rgba(255,255,255,0.9)' : '#666'
                    }}>
                      Tests
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: '28px', 
                    color: isExpanded ? 'white' : currentConfig.color,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}>
                    ▼
                  </div>
                </div>
              </div>

              {/* Tests Grid - Expandable */}
              {isExpanded && (
                <div style={{ 
                  padding: '30px',
                  background: 'white',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {topicGroup.tests.map((test, testIndex) => (
                    <div 
                      key={test.examId}
                      className="card" 
                      style={{ 
                        padding: '25px',
                        background: 'white',
                        border: `2px solid ${currentConfig.color}40`,
                        transition: 'all 0.3s',
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = `0 8px 16px ${currentConfig.color}30`;
                        e.currentTarget.style.borderColor = currentConfig.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = `${currentConfig.color}40`;
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: currentConfig.color,
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold'
                      }}>
                        Test {testIndex + 1}
                      </div>
                      
                      <h4 style={{ 
                        fontSize: '18px', 
                        marginBottom: '10px', 
                        color: currentConfig.color,
                        fontWeight: 'bold',
                        paddingRight: '60px'
                      }}>
                        {test.title}
                      </h4>
                      
                      <p style={{ 
                        fontSize: '14px', 
                        lineHeight: '1.5', 
                        color: '#666',
                        marginBottom: '15px',
                        minHeight: '42px'
                      }}>
                        {test.description}
                      </p>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        background: `${currentConfig.color}10`,
                        borderRadius: '6px',
                        marginBottom: '15px'
                      }}>
                        <div>
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Questions</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: currentConfig.color }}>
                            {test.questionCount}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Duration</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: currentConfig.color }}>
                            {test.duration}m
                          </div>
                        </div>
                      </div>
                      
                      {(() => {
                        const isTest2or3 = testIndex > 0;
                        const isEssentialLocked = userTier === 'essential' && isTest2or3;
                        
                        if (isEssentialLocked) {
                          return (
                            <Link to="/pricing" style={{ textDecoration: 'none' }}>
                              <button 
                                className="btn-primary"
                                style={{ 
                                  width: '100%', 
                                  padding: '12px', 
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  background: '#8b5cf6',
                                  border: 'none'
                                }}
                              >
                                🔒 Premium Only
                              </button>
                            </Link>
                          );
                        }
                        
                        if (userPremiumStatus || trialStatus.trialTopicsRemaining > 0) {
                          return (
                            <Link to={`/exam/${test.examId}`} style={{ textDecoration: 'none' }}>
                              <button 
                                className="btn-primary"
                                style={{ 
                                  width: '100%', 
                                  padding: '12px', 
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  background: currentConfig.color,
                                  border: 'none'
                                }}
                              >
                                Start Test
                              </button>
                            </Link>
                          );
                        }
                        
                        return (
                          <Link to="/pricing" style={{ textDecoration: 'none' }}>
                            <button 
                              className="btn-primary"
                              style={{ 
                                width: '100%', 
                                padding: '12px', 
                                fontSize: '14px',
                                fontWeight: 'bold',
                                background: '#ef4444',
                                border: 'none'
                              }}
                            >
                              🔒 Upgrade to Access
                            </button>
                          </Link>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        
        {getTopicsForSubject().length === 0 && (
          <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📚</div>
            <h3 style={{ fontSize: '24px', color: '#666', marginBottom: '10px' }}>No tests available yet</h3>
            <p style={{ fontSize: '16px', color: '#999' }}>
              Tests for {selectedSubject} are coming soon!
            </p>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="card" style={{ 
        marginTop: '40px',
        padding: '30px',
        background: getCurrentSubjectConfig().color,
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '36px', marginBottom: '15px' }}>💡</div>
        <h3 style={{ fontSize: '24px', marginBottom: '12px' }}>How Topic Practice Works</h3>
        <p style={{ fontSize: '16px', opacity: '0.95', maxWidth: '800px', margin: '0 auto', lineHeight: '1.7' }}>
          Each topic test focuses on specific skills with 10 carefully selected questions. 
          Practice one topic at a time to build mastery, or mix and match to keep things interesting. 
          Your progress is tracked so you can see which topics need more attention!
        </p>
      </div>
    </div>
  );
}

export default TopicTests;
